import { useState, useCallback, useEffect } from 'react';
import { MIDASProject } from '../../types/project.types';
import { PaneNode } from '../../types/pane.types';
import { SortCondition, createColumnName } from '../../types';
import { ProjectState } from '../../types';
import { useProjectStore } from '../stores/projectStore';
import logger from '../utils/logger';
import { useSaveFileModal } from './useSaveFileModal';
import { saveProject } from '../services/storageService';
import { compressGzip } from '../services/compressionService';
import { createTransientAwareReplacer } from '../../types';

// DatasetSelection type definition
interface DatasetSelection {
  selectedColumns: string[];
  selectedRows: number[];
}

/**
 * Web版プロジェクトの永続化（保存・エクスポート）を管理するフック
 *
 * 責務:
 * - プロジェクトのOPFSへの保存
 * - プロジェクトのダウンロード（エクスポート）
 * - プロジェクト状態の更新
 *
 * 注意: projectStoreに統合されたため、ワークスペース同期は不要
 */
interface UseProjectPersistenceProps {
  projectId: string;
  project: MIDASProject | null;
  layout: {
    rootPane: PaneNode;
    activeContainerId: string | null | undefined;
  };
  getCurrentActiveDatasetSelection: () => DatasetSelection;
  getCurrentSortConditions: () => SortCondition[];
  getActiveDataset: () => { id: string } | null;
  onProjectUpdate: (project: MIDASProject) => void;
}

export function useProjectPersistence({
  projectId: _projectId,
  project,
  layout,
  getCurrentActiveDatasetSelection,
  getCurrentSortConditions,
  getActiveDataset,
  onProjectUpdate
}: UseProjectPersistenceProps) {
  const [isSaving, setIsSaving] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const { isOpen: isSaveModalOpen, openModal, closeModal } = useSaveFileModal();
  const [pendingProject, setPendingProject] = useState<MIDASProject | null>(null);

  /**
   * プロジェクトの状態を準備（選択状態、ソート条件などを含む）
   */
  const prepareProjectState = useCallback((): MIDASProject | null => {

    if (!project) {
      logger.error('[useProjectPersistence] Project is null');
      return null;
    }

    const activeDataset = getActiveDataset();

    if (!activeDataset) {
      logger.error('[useProjectPersistence] アクティブなデータセットが存在しません');
      return null;
    }

    const currentSelection = getCurrentActiveDatasetSelection();

    // アクティブなタブIDを取得
    let activeTabId = '';
    if (layout.rootPane && layout.rootPane.type === 'container') {
      activeTabId = layout.rootPane.activeTabId || (layout.rootPane.tabs[0]?.id ?? '');
    } else if (layout.rootPane && layout.rootPane.type === 'split') {
      // 分割ペインの場合、再帰的にアクティブなコンテナを探す
      const findActiveContainer = (node: PaneNode): { activeTabId?: string } | null => {
        if (node.type === 'container') {
          return { activeTabId: node.activeTabId };
        }
        if (node.type === 'split') {
          return findActiveContainer(node.rightChild) || findActiveContainer(node.leftChild);
        }
        return null;
      };
      const activeContainer = findActiveContainer(layout.rootPane);
      activeTabId = activeContainer?.activeTabId || '';
    }

    const updatedProjectState: ProjectState = {
      datasetStates: {
        [activeDataset.id]: {
          filters: [],
          sorts: getCurrentSortConditions(),
          columnConfigurations: [],
          selectedColumns: currentSelection.selectedColumns.map(createColumnName),
          selectedRows: currentSelection.selectedRows,
          viewState: {
            columnWidths: { /* no-op */ },
            hiddenColumns: []
          }
        }
      },
      viewState: {
        activeTab: activeTabId
      },
      metadata: {
        createdAt: project.metadata.createdAt,
        lastModified: new Date().toISOString(),
        version: project.version,
        description: project.metadata.description
      }
    };

    // projectStoreから最新のworkspaces状態を取得
    const projectStore = useProjectStore.getState();
    const currentProject = projectStore.project;

    // ワークスペース状態を含めてプロジェクトを更新
    const updatedProject: MIDASProject = {
      ...project,
      projectState: updatedProjectState,
      workspaces: currentProject?.workspaces ?? project.workspaces,
      activeWorkspaceId: currentProject?.activeWorkspaceId ?? project.activeWorkspaceId,
      metadata: {
        ...project.metadata,
        lastModified: new Date().toISOString()
      }
    };

    return updatedProject;
  }, [project, getActiveDataset, getCurrentActiveDatasetSelection, getCurrentSortConditions, layout.rootPane]);

  /**
   * プロジェクトをOPFSに保存（内部保存）
   * Web版では自動的にOPFSに保存される
   */
  const saveProjectToFile = useCallback(async (): Promise<boolean> => {

    if (!project) {
      logger.error('[useProjectPersistence] project not available');
      return false;
    }

    setIsSaving(true);
    try {
      const updatedProject = prepareProjectState();
      if (!updatedProject) {
        return false;
      }


      // OPFSに保存
      await saveProject(updatedProject);

      logger.info('[useProjectPersistence] プロジェクトがOPFSに保存されました');
      setLastSavedAt(new Date());
      onProjectUpdate(updatedProject);

      // ProjectStoreも更新
      useProjectStore.getState().loadProject(updatedProject);

      setIsSaving(false);
      return true;
    } catch (error) {
      logger.error('[useProjectPersistence] プロジェクト保存エラー:', error);
      setIsSaving(false);
      return false;
    }
  }, [project, prepareProjectState, onProjectUpdate]);

  /**
   * プロジェクトをエクスポート（ダウンロード）
   * SaveFileModalを表示してファイル名を入力させる
   */
  const saveProjectToFileAs = useCallback(async (): Promise<boolean> => {

    if (!project) {
      logger.error('[useProjectPersistence] project not available');
      return false;
    }

    setIsSaving(true);
    try {
      const updatedProject = prepareProjectState();
      if (!updatedProject) {
        return false;
      }

      // カスタムモーダルを使用してファイル保存
      setPendingProject(updatedProject);
      openModal();
      setIsSaving(false); // モーダルが開いたら保存状態をリセット
      return true; // モーダルで処理を継続

      // 実際の保存処理はhandleSaveCallbackで行われる
    } catch (error) {
      logger.error('[useProjectPersistence] プロジェクト保存エラー:', error);
      setIsSaving(false);
      return false;
    }
  }, [project, prepareProjectState, openModal]);

  /**
   * SaveFileModalからのコールバックで実際のダウンロード処理を行う
   */
  const handleSaveCallback = useCallback(async (fileName: string) => {
    logger.info('[useProjectPersistence] ファイルをダウンロード', { fileName });

    if (!pendingProject) {
      logger.error('[useProjectPersistence] 保存するプロジェクトが見つかりません');
      return;
    }

    try {
      // transientプロパティを除外してシリアライズ（minified）
      // DerivedDataSetの__transient__マーカーにより、dataプロパティが自動的に除外される
      const jsonString = JSON.stringify(pendingProject, createTransientAwareReplacer());

      // gzip圧縮
      const compressed = await compressGzip(jsonString);

      // Blobを作成してダウンロード
      const blob = new Blob([compressed], { type: 'application/gzip' });
      const url = URL.createObjectURL(blob);

      // ダウンロードリンクを作成
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName.endsWith('.mds') ? fileName : `${fileName}.mds`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      // URLをクリーンアップ
      URL.revokeObjectURL(url);

      logger.info('[useProjectPersistence] プロジェクトがダウンロードされました:', a.download);
      setLastSavedAt(new Date());
      onProjectUpdate(pendingProject);

      // ProjectStoreも更新
      useProjectStore.getState().loadProject(pendingProject);

      setPendingProject(null);
    } catch (error) {
      logger.error('[useProjectPersistence] プロジェクトダウンロードエラー:', error);
    } finally {
      setIsSaving(false);
    }
  }, [pendingProject, onProjectUpdate]);

  /**
   * プロジェクト状態を更新（メモリ内のみ）
   */
  const updateProjectState = useCallback(async (updatedProject: MIDASProject): Promise<boolean> => {

    try {
      // フロントエンドの状態を更新
      onProjectUpdate(updatedProject);

      // ProjectStoreも更新
      useProjectStore.getState().loadProject(updatedProject);

      return true;
    } catch (error) {
      logger.error('[useProjectPersistence] プロジェクト状態更新エラー:', error);
      return false;
    }
  }, [onProjectUpdate]);

  /**
   * プロジェクトを再読み込み（Web版では未実装）
   */
  const reloadProject = useCallback(async (): Promise<MIDASProject | null> => {
    logger.warn('[useProjectPersistence] Web版では再読み込みは未実装です');
    return project;
  }, [project]);

  /**
   * キーボードショートカットによる保存を監視
   */
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ctrl+Shift+S または Cmd+Shift+S でエクスポート（先に評価）
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && (e.key === 'S' || e.key === 's')) {
        e.preventDefault();
        logger.info('[useProjectPersistence] Triggering export (Cmd+Shift+S)');
        saveProjectToFileAs();
        return;
      }
      // Ctrl+S または Cmd+S で保存
      if ((e.ctrlKey || e.metaKey) && (e.key === 's' || e.key === 'S')) {
        e.preventDefault();
        logger.info('[useProjectPersistence] Triggering save (Cmd+S)');
        saveProjectToFile();
        return;
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [saveProjectToFile, saveProjectToFileAs]);

  // 注意: レイアウト変更時のワークスペース同期は不要
  // projectStoreにworkspaceが統合されたため、レイアウト操作は直接projectに反映される

  return {
    // 状態
    isSaving,
    lastSavedAt,
    isSaveModalOpen,
    pendingProject,

    // アクション
    saveProjectToFile,
    saveProjectToFileAs,
    updateProjectState,
    reloadProject,
    prepareProjectState,
    handleSaveCallback,
    closeSaveModal: closeModal
  };
}
